/**
 * Extracts URL context from markdown containing <url-text> tags.
 *
 * Pattern: <url-text>CONTEXT</url-text>[[1]](url1)[[2]](url2)...
 *
 * @param {string} markdown - Markdown text potentially containing url-text tags
 * @returns {Map<string, string>} Map of URL to context text
 *
 * @example
 * const markdown = '<url-text>IC50 = 5.2 nM</url-text>[[1]](chembl:ASSAY-123)[[2]](chembl:ASSAY-456)';
 * const context = extractUrlContext(markdown);
 * // context.get('chembl:ASSAY-123') === 'IC50 = 5.2 nM'
 * // context.get('chembl:ASSAY-456') === 'IC50 = 5.2 nM'
 */
export function extractUrlContext(markdown) {
  const urlContextMap = new Map();

  // Handle null, undefined, or empty markdown
  if (!markdown) {
    return urlContextMap;
  }

  // Pattern to match: <url-text>CONTEXT</url-text> followed by one or more [[N]](url) citations
  const urlTextPattern = /<url-text>([^<]+)<\/url-text>((?:\[\[\d+\]\]\([^)]+\))+)/g;

  // Pattern to extract individual URLs from the citation block
  const citationPattern = /\[\[\d+\]\]\(([^)]+)\)/g;

  let match;
  while ((match = urlTextPattern.exec(markdown)) !== null) {
    const context = match[1];
    const citationBlock = match[2];

    // Extract all URLs from this citation block
    let citationMatch;
    while ((citationMatch = citationPattern.exec(citationBlock)) !== null) {
      const url = citationMatch[1];
      urlContextMap.set(url, context);
    }
  }

  return urlContextMap;
}

/**
 * Extracts meaningful identifier from URL
 *
 * @param {string} url - URL to extract identifier from
 * @returns {string} Extracted identifier or original URL
 */
export function extractIdentifierFromUrl(url) {
  if (!url) {
    return '';
  }

  if (typeof url !== 'string') {
    return url || '';
  }

  // Handle Plex prefix URLs (chembl:, uniprot:, etc.)
  if (url.includes(':') && !url.includes('://')) {
    const parts = url.split(':');
    const prefix = parts[0].toLowerCase();
    const identifier = parts.slice(1).join(':');

    // Only process known prefixes
    if (prefix === 'pubmed') {
      return `PMID:${identifier}`;
    }
    if (prefix === 'doi') {
      return `DOI:${identifier}`;
    }
    if (['chembl', 'uniprot', 'plex'].includes(prefix)) {
      return identifier;
    }

    // For unknown prefixes, return original URL
    return url;
  }

  // Handle HTTP(S) URLs - extract domain
  if (url.startsWith('http://') || url.startsWith('https://')) {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname;
    } catch {
      return url; // Fallback if URL parsing fails
    }
  }

  // Fallback: return original URL
  return url;
}

/**
 * Helper to check if string is numeric
 *
 * @param {string} str - String to check
 * @returns {boolean} True if string represents a valid number (integer, decimal, or negative)
 */
export function isNumeric(str) {
  if (!str || typeof str !== 'string') {
    return false;
  }

  // Trim whitespace
  const trimmed = str.trim();
  if (trimmed === '') {
    return false;
  }

  // Use parseFloat and check if result is finite
  // This handles integers, decimals, and negative numbers
  return !isNaN(trimmed) && isFinite(trimmed);
}